import { BaseApiService } from '@/shared/api/services/base.service';
import { buildUrl, type FilterValue } from '@/shared/lib/query-params';
import type {
  Project,
  CreateProjectRequest,
  UpdateProjectRequest,
  JsonApiResponse,
  PaginationParams,
  SortParams,
  FilterParams,
  IncludeParams,
} from '@/types/project';
import type { Concept } from '@/types/concept';
import type { BuilderSlotsMeta } from '@/types/store';

interface ProjectsListParams
  extends PaginationParams,
    SortParams,
    FilterParams,
    IncludeParams {}

/**
 * `POST /v1/projects/{id}/activate-builder` response: the updated project
 * (with the server-computed `pack` block flipped to active) plus the Builder
 * Pass slot counter in `meta.builder_slots`. Builder-Pass-only flow; 422s
 * (BUILDER_PASS_REQUIRED / BUILDER_MONTHLY_SLOTS_EXCEEDED) carry their paywall
 * inline like every other typed limit.
 */
export interface BuilderActivationResponse {
  data: Project;
  meta?: { builder_slots?: BuilderSlotsMeta };
}

class ProjectsService extends BaseApiService {
  async list(params?: ProjectsListParams): Promise<JsonApiResponse<Project[]>> {
    const url = buildUrl('/v1/projects', {
      include: params?.include,
      sort: params?.sort,
      filter: params?.filter as Record<string, FilterValue> | undefined,
      page:
        params?.number || params?.size
          ? { number: params?.number, size: params?.size }
          : undefined,
    });
    return this.httpGet<JsonApiResponse<Project[]>>(url);
  }

  async create(name: string): Promise<JsonApiResponse<Project>> {
    const request: CreateProjectRequest = {
      data: {
        type: 'project',
        attributes: { name },
      },
    };
    return this.post<JsonApiResponse<Project>>('/v1/projects', request);
  }

  async get(projectId: string): Promise<JsonApiResponse<Project>> {
    return this.httpGet<JsonApiResponse<Project>>(`/v1/projects/${projectId}`);
  }

  // `keepalive` lets the PATCH survive page unload / hard navigation — used by
  // the goals page on-unmount flush so fast back-press doesn't strand unsent edits.
  async update(
    projectId: string,
    attributes: Partial<UpdateProjectRequest['data']['attributes']>,
    options?: { keepalive?: boolean }
  ): Promise<JsonApiResponse<Project>> {
    const request: UpdateProjectRequest = {
      data: {
        type: 'project',
        attributes,
      },
    };
    return this.put<JsonApiResponse<Project>>(
      `/v1/projects/${projectId}`,
      request,
      options?.keepalive ? { keepalive: true } : undefined
    );
  }

  async delete(projectId: string): Promise<void> {
    await this.httpDelete(`/v1/projects/${projectId}`);
  }

  async restore(projectId: string): Promise<void> {
    await this.post(`/v1/projects/${projectId}/restore`);
  }

  // Builder Pass: spend one monthly building slot to unlock this building.
  // Idempotent per building server-side (a building already carrying a pack
  // returns its existing row without burning a slot).
  async activateBuilder(projectId: string): Promise<BuilderActivationResponse> {
    return this.post<BuilderActivationResponse>(
      `/v1/projects/${projectId}/activate-builder`
    );
  }

  async enhanceGoals(
    projectId: string,
    goals: string
  ): Promise<{ data: { enhanced_goals: string } }> {
    return this.post(
      `/v1/projects/${projectId}/enhance-goals`,
      this.formatJsonApiData('goal-enhancement', { goals })
    );
  }

  async listConcepts(
    projectId: string,
    params?: ProjectsListParams
  ): Promise<JsonApiResponse<Concept[]>> {
    const url = buildUrl(`/v1/projects/${projectId}/concepts`, {
      include: params?.include,
      sort: params?.sort,
      filter: params?.filter as Record<string, FilterValue> | undefined,
      page:
        params?.number || params?.size
          ? { number: params?.number, size: params?.size }
          : undefined,
    });
    return this.httpGet<JsonApiResponse<Concept[]>>(url);
  }
}

export const projectsService = new ProjectsService();
